import * as Sentry from '@sentry/browser'
import { debounce } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { sanitizeFenForEngine } from '../../../chess/fen'
import Engine, { ENGINE_URL } from './engine'

interface UseStockfishOptions {
    fen: string
    lines: number
    depth: number
    isEngineActive: boolean
}

export interface AnalysisResult {
    startFen: string
    depth: string
    scoreType: string
    score: string
    moves: string[]
}

export type StockEngineResultType = Record<string, AnalysisResult>

interface UseStockfishOutput {
    isEngineLoaded: boolean
    isEngineFailed: boolean
    allVariations: StockEngineResultType
    isCalculating: boolean
}

interface Search {
    fen: string
    lines: number
    depth: number
}

const defaultValues = (fen: string, lines: number): StockEngineResultType => {
    const variations: StockEngineResultType = {}
    for (let i = 1; i <= lines; i++) {
        variations[`v${i}`] = { startFen: fen, depth: '0', scoreType: '', score: '0', moves: [] }
    }
    return variations
}

// Load failures (offline, HTML served instead of the engine JS/wasm) are transient and not ours: breadcrumb only.
// Messages differ per engine: Chromium/Firefox vs WebKit ('Load failed', "module doesn't start with '\0asm'").
const engineErrorKind = (error = '') =>
    /SIMD|opcode|WebAssembly is not defined|variable: WebAssembly/i.test(error)
        ? 'unsupported'
        : !error ||
            /network|failed to (load|fetch)|load failed|fetching of the wasm|magic word|parse at byte 0:|\\0asm|'<'/i.test(error)
          ? 'network'
          : 'crash'
const reportedKinds = new Set<string>()

export function useStockfish({ fen, lines, depth, isEngineActive }: UseStockfishOptions): UseStockfishOutput {
    const [isEngineLoaded, setEngineIsLoaded] = useState(false)
    const [isEngineFailed, setIsEngineFailed] = useState(false)
    const [isCalculating, setIsCalculating] = useState(true)
    const [allVariations, setAllVariations] = useState<StockEngineResultType>(defaultValues(fen, lines))
    const engineRef = useRef<Engine | null>(null)
    // One `go` = one `bestmove`. A `position` sent before the stopped search's bestmove makes that search report
    // on the new board, so a new search waits in `queued` (latest wins) until the running one's bestmove arrives.
    const searchRef = useRef<{ running: Search | null; queued: Search | null }>({ running: null, queued: null })

    const startSearch = (engine: Engine, search: Search) => {
        searchRef.current.running = search
        engine.send(`position fen ${sanitizeFenForEngine(search.fen)}`)
        engine.send(`setoption name MultiPV value ${search.lines}`)
        engine.send(`go depth ${search.depth}`)
    }

    const requestSearch = (search: Search) => {
        const engine = initializeEngine()
        const state = searchRef.current
        if (!state.running) {
            startSearch(engine, search)
            return
        }
        if (!state.queued) {
            engine.send('stop')
        }
        state.queued = search
    }

    // Report engine failures once per session and kind; the console log doubles as a breadcrumb.
    const reportEngineError = (error: string) => {
        const kind = engineErrorKind(error)
        const { running } = searchRef.current
        console.log(`Stockfish -> ${kind}: ${error} Lines: ${running?.lines} FEN: ${running?.fen} `)
        setIsEngineFailed(true)
        if (kind !== 'network' && !reportedKinds.has(kind)) {
            reportedKinds.add(kind)
            Sentry.captureMessage(`Stockfish error: ${error}`, {
                level: 'warning',
                fingerprint: ['stockfish-engine', kind],
                tags: { engine_build: ENGINE_URL },
            })
        }
    }

    // Create or get an existing engine instance.
    const initializeEngine = () => {
        if (!engineRef.current) {
            setIsEngineFailed(false)
            searchRef.current = { running: null, queued: null }
            // The constructor gets only the reporter: a synchronous Worker failure must not re-enter this function.
            const engine = new Engine(ENGINE_URL, reportEngineError)
            engineRef.current = engine

            engine.error = (error: string) => {
                reportEngineError(error)
                if (engineRef.current === engine) {
                    // A trapped wasm instance is dead: drop it; the next position gets a fresh worker.
                    const { queued } = searchRef.current
                    engine.quit()
                    engineRef.current = null
                    queued && requestSearch(queued)
                }
            }

            // Subscribe to engine data.
            engine.stream = (data: string) => {
                const { running, queued } = searchRef.current
                if (engineRef.current !== engine) {
                    return
                }
                if (data.startsWith('bestmove')) {
                    searchRef.current.running = null
                    if (queued) {
                        searchRef.current.queued = null
                        startSearch(engine, queued)
                    } else if (running && data.includes('bestmove (none)')) {
                        setAllVariations(defaultValues(running.fen, running.lines))
                    }
                    return
                }
                // While a stop is pending, info lines still describe the previous position.
                if (!running || queued) {
                    return
                }

                const handleVariation = (multipv: number) => {
                    const matchPV = data.match(/^info .+ pv (.+)/)
                    const matchDepth = data.match(/^info depth (\d+)/)
                    const matchScore = data.match(/^info .+ score (cp|mate) (-?\d+)/)
                    if (matchPV && matchDepth && matchScore) {
                        const moves = matchPV[1].split(' ')
                        const depth = matchDepth[1]
                        const scoreType = matchScore[1]
                        const score = matchScore[2]
                        setAllVariations((prev) => ({
                            ...prev,
                            [`v${multipv}`]: { startFen: running.fen, depth, scoreType, score, moves },
                        }))
                    }
                }

                for (let i = 1; i <= running.lines; i++) {
                    if (data.includes(`multipv ${i}`)) {
                        handleVariation(i)
                    }
                }
            }

            // Setup engine with UCI options.
            engine.send(`setoption name Skill Level value 19`)
            engine.send('setoption name Use NNUE value true')
        }
        return engineRef.current
    }

    useEffect(() => {
        if (isEngineActive) {
            initializeEngine()
            setEngineIsLoaded(true)
            return () => {
                stopEngine()
            }
        }
    }, [isEngineActive])

    useEffect(() => {
        if (isEngineActive && fen) {
            setIsCalculating(true)
            requestSearch({ fen, lines, depth })
        }
    }, [fen, depth, lines, isEngineActive])

    useEffect(() => {
        setAllVariations(defaultValues(fen, lines))
    }, [lines, fen])

    // Until the reset above runs, state still holds the previous position's lines for one commit.
    const variations = useMemo(
        () => (allVariations.v1?.startFen === fen ? allVariations : defaultValues(fen, lines)),
        [allVariations, fen, lines],
    )

    const debouncedIsCalculating = useCallback(
        debounce(() => {
            setIsCalculating(false)
        }, 500),
        []
    )

    useEffect(() => {
        debouncedIsCalculating()
    }, [allVariations])

    const stopEngine = useCallback(() => {
        setEngineIsLoaded(false)
        if (engineRef.current) {
            engineRef.current.quit()
            engineRef.current = null
        }
    }, [])

    return { allVariations: variations, isEngineLoaded, isEngineFailed, isCalculating }
}
